import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Lobby, Event } from '@/api';
import { UserRole } from '@sportspredict/shared';
import { useIsMobile } from '@/hooks';
import { ROUTES } from '@/constants';

interface UseH2HLobbyRedirectProps {
	eventId: string | undefined;
	lobbyId: string | undefined;
	currentUser: any;
}

/**
 * Hook that redirects users who haven't joined an H2H lobby back to the event details page.
 * Used on all H2H pages except the main page (which handles auto-join from shared links).
 */
export function useH2HLobbyRedirect({ eventId, lobbyId, currentUser }: UseH2HLobbyRedirectProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const isMobile = useIsMobile();

	const [lobbyResponse, setLobbyResponse] = useState<any>(null);
	const [eventResponse, setEventResponse] = useState<any>(null);

	useEffect(() => {
		if (!eventId || !lobbyId) return;
		Event.get(eventId).then(setEventResponse).catch(console.error);
		Lobby.get(lobbyId).then(setLobbyResponse).catch(console.error);
	}, [eventId, lobbyId]);

	const isAdmin = currentUser?.role === UserRole.Admin;
	const hasCurrentUserJoinedLobby = useMemo(
		() => !!lobbyResponse?.lobby_users?.some((u: any) => u.user_id === currentUser?.id),
		[lobbyResponse?.lobby_users, currentUser?.id]
	);

	// Redirect user if they haven't joined the lobby
	useEffect(() => {
		if (isAdmin) return;
		if (lobbyResponse?.is_revival_initiated) return;

		const shouldRedirect = !!lobbyResponse && !!eventResponse && !hasCurrentUserJoinedLobby;

		if (!shouldRedirect) return;

		const timer = setTimeout(() => {
			if (isMobile) {
				navigate(`${ROUTES.EVENT_DETAILS}?id=${eventId}`);
			} else {
				const backPath = location.state?.isFromHomePage ? ROUTES.HOME : ROUTES.EVENTS;
				navigate(backPath, { state: { openPanelForEventId: eventId } });
			}
		}, 500);

		return () => clearTimeout(timer);
	}, [
		isAdmin,
		lobbyResponse,
		eventResponse,
		hasCurrentUserJoinedLobby,
		isMobile,
		navigate,
		eventId,
		location.state?.isFromHomePage,
	]);
}
